import React, { useState, useEffect, useRef } from 'react';
// User context
import { useUsersContext } from '@src/contexts/users';
import { UsersActions } from '@src/contexts/users/types';
// Types
import type { UsersIntergrationType, ErrorObject, DynamicsBcUsersApiData } from './types';
import type { AxiosResponse } from 'axios';
// API
import { allCompanies, setAllUsersIntegration, testConnection } from '@src/Api';
// Styles
import { Row, Col, Container } from 'react-bootstrap';
// Components
import RentmanForm from './rentman';
import DynamicsBcForm from './dynamicsBc';
import { useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { CustomToast } from '@src/components/toast';
import type { DynamicsBcCompany } from '../settings/dynamics_BC/invoice/types';

const defaultConfig = {
  users_integrations_type: '',
  users_integrations_api_data: {
    apiKey: '',
    apiSecret: '',
    apiHookKey: '',
  },
  users_integrations_connected: '',
};

export const UserConfigPage: React.FC = () => {
  const location = useLocation();
  const state = location.state as { error: string } | undefined;
  const [showToast, setShowToast] = React.useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState<{ rentman: boolean; dynamicsBc: boolean }>({ rentman: false, dynamicsBc: false });
  const [validationError, setValidationError] = useState<{ [fieldId: string]: boolean }>({});
  const [companies, setCompanies] = useState<DynamicsBcCompany[] | undefined>(undefined);
  const connectionRevision = useRef(0);
  const { usersDispatch } = useUsersContext();
  const [config, setConfig] = useState<UsersIntergrationType>({
    rentman: { ...defaultConfig },
    dynamicsBc: { ...defaultConfig, users_integrations_api_data: { clientId: '', tenantId: '', clientSecret: '', environmentName: 'production' } },
  });
  const [statusText, setStatusText] = useState<{ [x: string]: string }>({
    rentman: '...',
    dynamicsBc: '...',
  });
  const [loading, setLoading] = useState<{ [x: string]: boolean }>({
    rentman: false,
    dynamicsBc: false,
  });
  const [errorObject, setErrorObject] = useState<ErrorObject>({
    rentman: null,
    dynamicsBc: null,
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleResponse = async (response: AxiosResponse | null, type: 'rentman' | 'dynamicsBc'): Promise<void> => {
    if (response && typeof response.data === 'object') {
      if ('error' in response.data) {
        setSubmissionSuccess((prev) => ({ ...prev, [type]: false }));
        const errorMessage = typeof response.data.error === 'string' ? response.data.error : JSON.stringify(response.data.error);

        setStatusText({
          ...statusText,
          [type]: ` ${response.status}: ${errorMessage}`,
        });
        setErrorObject({
          ...errorObject,
          [type]: response.data,
        });
      } else {
        setStatusText({
          [type]: ` ${response.status}: ${response.data.message}`,
        });
        setErrorObject({
          ...errorObject,
          [type]: null,
        });
        if (response && response.status >= 200 && response.status < 300) {
          setStatusText({
            [type]: ` ${response.status}: ${response.data.message}`,
          });
          setSubmissionSuccess((prevState) => ({ ...prevState, [type]: true }));
        } else {
          setSubmissionSuccess((prevState) => ({ ...prevState, [type]: false }));
        }
      }

      if (response.data.integration) {
        setConfig((prevConfig) => ({
          ...prevConfig,
          [type]: response.data.integration,
        }));
      }

      if (response.data.token) {
        usersDispatch({ type: UsersActions.LOGIN, payload: response.data.token });
      }
    }
  };

  const handleRentmanValueChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setConfig({
      ...config,
      rentman: {
        ...config.rentman,
        users_integrations_api_data: {
          ...config.rentman.users_integrations_api_data,
          [event.target.id]: event.target.value,
        },
      },
    });
  };

  const handleDynamicsBcValueChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { id, value } = event.target;
    connectionRevision.current += 1;
    setSubmissionSuccess((prev) => ({ ...prev, dynamicsBc: false }));
    setCompanies(undefined);
    setConfig((prev) => {
      const api = { ...prev.dynamicsBc.users_integrations_api_data } as DynamicsBcUsersApiData;
      if (id === 'environmentName' || id === 'tenantId') delete api.selectedCompany;
      return {
        ...prev,
        dynamicsBc: { ...prev.dynamicsBc, users_integrations_api_data: { ...api, [id]: value } },
      };
    });
  };

  const handleDynamicsBcCompanyChanged = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCompany = event.target.value;
    if (!selectedCompany || !submissionSuccess.dynamicsBc) return;
    const revision = connectionRevision.current;
    const newConfig = {
      ...config,
      dynamicsBc: {
        ...config.dynamicsBc,
        users_integrations_api_data: {
          ...config.dynamicsBc.users_integrations_api_data,
          selectedCompany,
        },
      },
    };
    setLoading((prev) => ({ ...prev, dynamicsBc: true }));
    try {
      const response = await setAllUsersIntegration(newConfig, 'dynamicsBc');
      if (revision === connectionRevision.current) await handleResponse(response, 'dynamicsBc');
    } finally {
      setLoading((prev) => ({ ...prev, dynamicsBc: false }));
      setFormSubmitted(true);
    }
  };

  const handleValidationError = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const fieldId = event.target.name;
    setValidationError({
      ...validationError,
      [fieldId]: value.trim() === '',
    });
  };

  const handleFormSubmit = async (e: React.MouseEvent<HTMLButtonElement>, type: 'rentman' | 'dynamicsBc'): Promise<void> => {
    e.preventDefault();
    if (type === 'dynamicsBc') {
      const environment = (config.dynamicsBc.users_integrations_api_data as DynamicsBcUsersApiData).environmentName;
      if (!environment?.trim() || /[/\\?#]/.test(environment)) {
        setValidationError((prev) => ({ ...prev, environmentName: true }));
        return;
      }
      setValidationError((prev) => ({ ...prev, environmentName: false }));
    }
    const revision = connectionRevision.current;

    setLoading((prev) => ({ ...prev, [type]: true }));

    const response = await setAllUsersIntegration(config, type);
    if (type !== 'dynamicsBc' || revision === connectionRevision.current) await handleResponse(response, type);

    setLoading((prev) => ({ ...prev, [type]: false }));

    setFormSubmitted(true);
  };

  //Function to fetch all companies from Dynamics BC for user
  const fetchCompanies = async () => {
    const revision = connectionRevision.current;
    setLoading((prev) => ({ ...prev, dynamicsBc: true }));
    try {
      const response = await allCompanies();
      if (revision === connectionRevision.current && response && Array.isArray(response.data)) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading((prev) => ({ ...prev, dynamicsBc: false }));
    }
  };

  //Use effect hook for fetching companies upon entering correct values
  useEffect(() => {
    if (submissionSuccess.dynamicsBc === true) {
      fetchCompanies();
    }
  }, [submissionSuccess.dynamicsBc]);

  useEffect(() => {
    if (state && state.error) {
      setShowToast(true);
    }
  }, [state]);

  useEffect(() => {
    const getIntegrationHandler = async (): Promise<void> => {
      const testRentmanConnectionResponse = await testConnection('rentman');
      await handleResponse(testRentmanConnectionResponse, 'rentman');

      const revision = connectionRevision.current;
      const testDynamicsBcConnectionResponse = await testConnection('dynamicsBc');
      if (revision === connectionRevision.current) await handleResponse(testDynamicsBcConnectionResponse, 'dynamicsBc');
    };
    getIntegrationHandler();
  }, []);

  return (
    <>
      <Container className="toast-b-right">
        <AnimatePresence>
          <CustomToast
            duration={6000}
            show={showToast}
            title="Error"
            message={location?.state?.error || 'An unknown error occurred'}
            type="error"
            onClose={() => setShowToast(false)}
          />
        </AnimatePresence>
      </Container>
      <Row className="mt-4">
        <Col sm={6}>
          <RentmanForm
            config={config}
            loading={loading}
            statusText={statusText}
            errorObject={errorObject}
            formSubmitted={formSubmitted}
            validationError={validationError}
            handleValidationError={handleValidationError}
            handleRentmanValueChanged={handleRentmanValueChanged}
            handleFormSubmit={handleFormSubmit}
            submissionSuccess={submissionSuccess}
            setSubmissionSuccess={setSubmissionSuccess}
          />
        </Col>

        <Col sm={6}>
          <DynamicsBcForm
            config={config}
            loading={loading}
            statusText={statusText}
            errorObject={errorObject}
            formSubmitted={formSubmitted}
            validationError={validationError}
            handleValidationError={handleValidationError}
            handleDynamicsBcValueChanged={handleDynamicsBcValueChanged}
            handleDynamicsBcCompanyChanged={handleDynamicsBcCompanyChanged}
            handleFormSubmit={handleFormSubmit}
            submissionSuccess={submissionSuccess}
            setSubmissionSuccess={setSubmissionSuccess}
            companies={companies}
          />
        </Col>
      </Row>
    </>
  );
};
